<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Períodos de Recesso</h5>
      <router-link
        v-if="hasPermission('adicionar_recessos')"
        to="/configuracoes/recessos/novo"
      >
        <b-button variant="primary" class="d-flex align-items-center">
          <i class="bi bi-calendar-plus-fill mr-2" title="Novo Recesso"></i>
        </b-button>
      </router-link>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <!-- Descrição -->
      <input
        v-model="filters.descricao"
        placeholder="Filtrar por descrição"
        class="form-control"
        @input="filtrar"
      />

      <!-- Aplicado -->
      <b-form-select
        v-model="filters.aplicado"
        :options="[
          { value: '',    text: 'Todos' },
          { value: true,  text: 'Aplicados'   },
          { value: false, text: 'Não Aplicados'   }
        ]"
        class="form-control"
        @change="filtrar"
      />

      <!-- Botão Limpar -->
      <b-button variant="secondary" @click="limparFiltros">
        <i class="bi bi-funnel mr-2" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('data_inicio')">
              Início {{ arrow('data_inicio') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('data_fim')">
              Fim {{ arrow('data_fim') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="changeSort('descricao')">
              Descrição {{ arrow('descricao') }}
            </button>
          </th>
          <th class="text-center">
            <button class="btn btn-link p-0" @click="changeSort('aplicado')">
              Aplicado {{ arrow('aplicado') }}
            </button>
          </th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="r in items" :key="r.id">
          <td>{{ formatDate(r.data_inicio) }}</td>
          <td>{{ formatDate(r.data_fim) }}</td>
          <td>{{ r.descricao }}</td>
          <td class="text-center">
            <i
              :class="r.aplicado
                ? 'bi bi-check-circle-fill text-success'
                : 'bi bi-x-circle-fill text-secondary'"
              :title="r.aplicado ? 'Sim' : 'Não'"
            ></i>
          </td>
          <td class="text-end">
            <!-- Detalhar -->
            <router-link
              v-if="hasPermission('detalhar_recessos')"
              :to="`/configuracoes/recessos/${r.id}/detalhar`"
            >
              <i class="bi bi-eye-fill text-primary mx-2" title="Detalhar"></i>
            </router-link>
            <!-- Aplicar / Cancelar -->
            <i
              v-if="hasPermission('aplicar_recessos') && !r.aplicado"
              class="bi bi-calendar-check-fill text-success mx-2"
              title="Aplicar Recesso"
              @click="openAplicarModal(r)"
            ></i>
            <i
              v-else-if="hasPermission('cancelar_recessos') && r.aplicado"
              class="bi bi-x-octagon-fill text-warning mx-2"
              title="Cancelar Aplicação"
              @click="openCancelarModal(r)"
            ></i>
            <!-- Editar -->
            <router-link
              v-if="hasPermission('alterar_recessos') && !r.aplicado"
              :to="`/configuracoes/recessos/${r.id}`"
            >
              <i class="bi bi-pencil-fill text-warning mx-2" title="Editar"></i>
            </router-link>
            <i
              v-else
              class="bi bi-pencil-fill text-secondary mx-2"
              title="Não é possível editar recesso aplicado"
            ></i>
            <!-- Excluir -->
            <i
              v-if="hasPermission('excluir_recessos') && !r.aplicado"
              class="bi bi-trash-fill text-danger mx-2"
              title="Excluir"
              @click="excluir(r.id)"
            ></i>
            <i
              v-else
              class="bi bi-trash-fill text-secondary mx-2"
              title="Não é possível excluir recesso aplicado"
            ></i>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @change="changeLimit"
        />
      </div>
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @input="load"
        />
      </div>
    </div>

    <!-- Modais -->
    <AplicarRecesso
      v-if="showAplicar && selectedRecesso"
      :mostrar="showAplicar"
      :recesso="selectedRecesso"
      @close="showAplicar = false"
      @aplicado="onAplicado"
    />
    <CancelarAplicacao
      v-if="showCancelar && selectedRecesso"
      :mostrar="showCancelar"
      :recesso="selectedRecesso"
      @close="showCancelar = false"
      @cancelado="onCancelado"
    />
    <AlertModal
      v-model:visible="showResumo"
      :title="resumoTitle"
      :message="resumoMessage"
      type="alert"
      @close="showResumo = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, getCurrentInstance } from 'vue'
import { BButton, BPagination, BFormSelect } from 'bootstrap-vue-next'
import axios from '@/services/axios'
import { useSortableTable } from '@/utils/useSortableTable'
import { usePermissions } from '@/composables/usePermissions'

import AplicarRecesso    from '@/components/modals/AplicarRecesso.vue'
import CancelarAplicacao from '@/components/modals/CancelarAplicacao.vue'
import AlertModal        from '@/components/AlertModal.vue'

const { proxy } = getCurrentInstance()
const { fetchPermissions, hasPermission } = usePermissions()

const {
  items,
  totalItems,
  page,
  limit,
  filters,
  load,
  sort: changeSort,
  arrow
} = useSortableTable(
  `/configuracoes/recessos`,
  { key: 'data_inicio', order: 'asc' }
)

const pageOptions = ref([10, 15, 20, 25, 50])
const startRow    = computed(() => (page.value - 1) * limit.value + 1)
const endRow      = computed(() => Math.min(page.value * limit.value, totalItems.value))

const showAplicar     = ref(false)
const showCancelar    = ref(false)
const selectedRecesso = ref(null)

const showResumo     = ref(false)
const resumoTitle   = ref('')
const resumoMessage = ref('')

// Inicializa filtros
onMounted(async () => {
  await fetchPermissions()
  filters.value = { descricao: '', aplicado: '' }
  load()
})

// Filtro e paginação
function filtrar() {
  page.value = 1
  load()
}
function limparFiltros() {
  filters.value.descricao = ''
  filters.value.aplicado  = ''
  filtrar()
}
watch(page, load)
watch(limit, () => { page.value = 1; load() })
function changeLimit() { page.value = 1; load() }

// Ações CRUD
async function excluir(id) {
  const confirmou = await proxy.$confirm('Tem certeza que deseja excluir este recesso?', 'Confirmação')
  if (!confirmou) return
  try {
    await axios.delete(`/configuracoes/recessos/${id}`)
    load()
    proxy.$toast.success('Recesso excluído com sucesso!')
  } catch (e) {
    const msg = e.response?.data?.message || 'Erro ao excluir recesso.'
    await proxy.$alert(msg, 'Erro')
  }
}

function openAplicarModal(recesso) {
  selectedRecesso.value = recesso
  showAplicar.value = true
}
function openCancelarModal(recesso) {
  selectedRecesso.value = recesso
  showCancelar.value = true
}

async function onAplicado(payload) {
  showAplicar.value = false
  load()
  resumoTitle.value = 'Resumo da Aplicação'
  resumoMessage.value = `
    ✅ <strong>Recesso aplicado com sucesso!</strong><br><br>
    <ul style="text-align:left; margin:0; padding-left:1.2em;">
      <li>(1) Término da vigência &gt; fim: <strong>${payload.affectedCaseA}</strong> matrículas</li>
      <li>(2) Término da vigência entre início e fim: <strong>${payload.affectedCaseB}</strong> matrículas</li>
    </ul>
  `
  showResumo.value = true
}

async function onCancelado(payload) {
  showCancelar.value = false
  load()
  resumoTitle.value = 'Resumo do Cancelamento'
  resumoMessage.value = `
    ℹ️ <strong>Aplicação de recesso cancelada.</strong><br><br>
    <ul style="text-align:left; margin:0; padding-left:1.2em;">
      <li>Revertido – término entre início e fim: <strong>${payload.revertedCaseB}</strong> matrículas</li>
      <li>Revertido – término &gt; fim: <strong>${payload.revertedCaseA}</strong> matrículas</li>
    </ul>
  `
  showResumo.value = true
}

// Formatação de data
function formatDate(date) {
  if (!date) return ''

  // Garante uso da data "pura", sem fuso
  const [yyyy, mm, dd] = date.substring(0, 10).split('-')
  return `${dd}/${mm}/${yyyy}`
}
</script>

<style scoped>
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
.table-hover tr:hover { background-color: var(--bs-light); }
</style>
